import { useState } from 'react';

import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconAngleDown } from '@iwoca/icons';
import { GetStatementsResponse } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Text
} from '@ui';
import { FormattedDate, FormattedMessage } from 'react-intl';

import messages from './FundingHistoryTable.messages';
import { getLoanDurationInMonths } from '../../../myOffers/MyOffers.helpers';

type TFundingHistory = GetStatementsResponse['data'][number]['funding_history'];

export const FundingHistoryTable = ({
    fundingHistory
}: {
    fundingHistory: TFundingHistory;
}) => {
    return (
        <Table data-testid="funding-history-table">
            <TableHeader>
                <TableRow>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.date} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.fundedAmount} />
                    </TableHead>

                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.drawdownFee} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.interestRate} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.duration} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.maxCreditLimit} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.principalAfterFunding} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.offerWindow} />
                    </TableHead>
                    <TableHead className="text-xs">
                        <FormattedMessage {...messages.interestOnlyPeriod} />
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {fundingHistory.map((funding, index) => (
                    <FundingHistoryTableRow key={index} funding={funding} />
                ))}
            </TableBody>
        </Table>
    );
};

const FundingHistoryTableRow = ({
    funding
}: {
    funding: TFundingHistory[number];
}) => {
    const [expanded, setExpanded] = useState(false);

    const fundingHasMultipleInterestRates = funding.interest_rates.length > 1;

    return (
        <>
            <TableRow>
                <TableCell>
                    <FormattedDate value={funding.start_date} />
                </TableCell>
                <TableCell>
                    <FormattedCurrency value={funding.amount} />
                </TableCell>
                <TableCell>
                    <FormattedCurrency value={funding.funding_fee_amount!} />
                </TableCell>
                {fundingHasMultipleInterestRates ? (
                    <TableCell className="flex items-center gap-xs">
                        {funding.interest_rates[0].rate * 100}%
                        <Button
                            size="small"
                            variant="tertiary"
                            onClick={() => setExpanded(!expanded)}
                            data-testid="expand-row"
                        >
                            <IconAngleDown />
                        </Button>
                    </TableCell>
                ) : (
                    <TableCell>
                        {funding.interest_rates[0].rate * 100}%
                    </TableCell>
                )}
                <TableCell>
                    {getLoanDurationInMonths({
                        amount: funding.duration.amount,
                        // @ts-expect-error funding.duration.unit is string, function expects 'days' | 'months' | 'years'
                        unit: funding.duration.unit
                    })}
                </TableCell>
                <TableCell>
                    <FormattedCurrency value={funding.max_credit_limit} />
                </TableCell>
                <TableCell>
                    <FormattedCurrency
                        value={funding.principal_after_funding}
                    />
                </TableCell>
                <TableCell>{funding.timeout_window_days}</TableCell>
                <TableCell>{funding.interest_only_repayments}</TableCell>
            </TableRow>
            {expanded && (
                <TableRow>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className="min-w-[150px]">
                        {funding.interest_rates.map((interestRate, index) => (
                            <Text className="font-medium" key={index}>
                                <FormattedDate value={interestRate.date} /> -{' '}
                                {interestRate.rate * 100}%
                            </Text>
                        ))}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                </TableRow>
            )}
        </>
    );
};
